import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@clerk/clerk-react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { BACKEND_URL } from "@/constants/api";
import { useAbortableLoader, isAbortError } from "@/hooks/useAbortableLoader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  LucidePlus,
  LucideUser,
  LucideShare2,
  LucideChevronLeft,
  LucideDownload,
  LucideSettings2,
  LucidePencil,
  LucideImage,
  LucideBell,
} from "lucide-react";
import { REPORT_FIELD_LABELS } from "@/reports/shared_utils/reportFieldLabels";
import { formatGrade } from "@/utils/formatGrade";
import { ExportModal } from "@/components/modals/ExportModal";
import { ShareReportFolderModal } from "@/components/modals/ShareReportFolderModal";
import { AddStudentModal } from "@/components/modals/AddStudentModal";
import { DeleteReportModal } from "@/components/modals/DeleteReportModal";
import { EditReportFolderModal } from "@/components/modals/EditReportFolderModal";
import { LimitExceededModal } from "@/components/modals/LimitExceededModal";
import { PageHeader } from "@/layouts/PageHeader";
import { useSubscription } from "@/context/SubscriptionContext";
import { StudentReportList } from "@/components/entities/StudentReportList";
import { StudentListSizeToggle } from "@/components/entities/StudentListSizeToggle";
import { useStudentListSize } from "@/hooks/useStudentListSize";

const REPORTS_PAGE_SIZE = 10;

export const OwnedReportFolder = () => {
  const { reportFolderId } = useParams();
  const { getToken } = useAuth();
  const navigate = useNavigate();

  const [reportFolder, setReportFolder] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [showAddStudentModal, setShowAddStudentModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [allStudentsForModal, setAllStudentsForModal] = useState([]);
  const [loadingModalStudents, setLoadingModalStudents] = useState(false);

  const [reportsPage, setReportsPage] = useState(0);
  const [sortField, setSortField] = useState("oen");
  const [sortOrder, setSortOrder] = useState("asc");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [totalReports, setTotalReports] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [showExportModal, setShowExportModal] = useState(false);
  const [allReportsForExport, setAllReportsForExport] = useState([]);
  const [cardToDelete, setCardToDelete] = useState(null);
  const [limitMessage, setLimitMessage] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [updatingFolder, setUpdatingFolder] = useState(false);

  const { reportsRemaining, loading: subLoading } = useSubscription();
  const atLimit = !subLoading && reportsRemaining === 0;

  const [listSize, setListSize] = useStudentListSize();

  const fetchReportFolder = useCallback(async (signal) => {
    try {
      const token = await getToken();
      const params = new URLSearchParams({
        sort: sortField,
        order: sortOrder,
        search: debouncedSearch,
        page: reportsPage,
        limit: REPORTS_PAGE_SIZE,
      });
      const res = await fetch(
        `${BACKEND_URL}api/report-folders/${reportFolderId}?${params}`,
        { headers: { Authorization: `Bearer ${token}` }, signal }
      );
      if (!res.ok) throw new Error("Failed to fetch Report Folder");
      const data = await res.json();
      setReportFolder(data);
      setReports(data.reports || []);
      setTotalReports(data.totalReports ?? 0);
      setTotalPages(data.totalPages ?? 1);
      setFetchError(null);
    } catch (err) {
      if (isAbortError(err)) return;
      console.error("Error fetching Report Folder:", err);
      setFetchError("Could not load this Report Folder. Please refresh the page.");
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [reportFolderId, sortField, sortOrder, debouncedSearch, reportsPage, getToken]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset to page 0 when sort or search changes
  useEffect(() => {
    setReportsPage(0);
  }, [sortField, sortOrder, debouncedSearch]);

  // Fetch whenever folder, sort, search, or page changes
  useAbortableLoader(fetchReportFolder);

  const handleCreate = async (studentName, oen, applyDefaults) => {
    const token = await getToken();
    const reqBody = { studentName, applyDefaults };
    if (oen) reqBody.oen = oen;
    const res = await fetch(
      `${BACKEND_URL}api/report-folders/${reportFolderId}/reports`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(reqBody),
      }
    );
    const resBody = await res.json().catch(() => ({}));
    if (res.status === 422 && resBody.limitExceeded) {
      setShowAddStudentModal(false);
      setLimitMessage(resBody.message);
      return;
    }
    if (res.status === 403) {
      throw new Error("Report card quota exceeded. Please upgrade your plan.");
    }
    if (!res.ok) throw new Error("Failed to create report card. Please try again.");
    navigate(`/report-folders/${reportFolderId}/reports/${resBody._id}`);
  };

  const openAddStudentModal = async () => {
    setShowAddStudentModal(true);
    setLoadingModalStudents(true);
    try {
      const token = await getToken();
      const params = new URLSearchParams({ sort: sortField, order: sortOrder, limit: 0 });
      const res = await fetch(
        `${BACKEND_URL}api/report-folders/${reportFolderId}?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error();
      const data = await res.json();
      setAllStudentsForModal(data.reports || []);
    } catch {
      setAllStudentsForModal(reports);
    } finally {
      setLoadingModalStudents(false);
    }
  };

  const handleDelete = (card) => {
    setCardToDelete(card);
  };

  const handleToggleComplete = async (card) => {
    const previous = card.checkedState ?? "incomplete";
    const next = previous === "complete" ? "incomplete" : "complete";
    setReports((prev) => prev.map((r) => (r._id === card._id ? { ...r, checkedState: next } : r)));
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}api/reports/${card._id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ checkedState: next }),
      });
      if (!res.ok) throw new Error("Update failed");
    } catch (err) {
      console.error("Error updating report status:", err);
      setReports((prev) => prev.map((r) => (r._id === card._id ? { ...r, checkedState: previous } : r)));
      toast.error("Failed to update status. Please try again.");
    }
  };

  const confirmDelete = async () => {
    const card = cardToDelete;
    setCardToDelete(null);
    try {
      const token = await getToken();
      await fetch(`${BACKEND_URL}api/reports/${card._id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      setReports((prev) => prev.filter((r) => r._id !== card._id));
    } catch (err) {
      console.error("Error deleting report card:", err);
      toast.error("Failed to delete report card. Please try again.");
    }
  };

  const handleUpdateFolder = async (folder, editForm, { cascadeGrade = false } = {}) => {
    setUpdatingFolder(true);
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}api/report-folders/${folder._id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ ...editForm, cascadeGrade }),
      });
      if (!res.ok) throw new Error("Failed to update Report Folder");
      const updated = await res.json();
      setReportFolder((prev) => (prev ? { ...prev, ...updated } : updated));
      if (cascadeGrade && editForm.grade) {
        setReports((prev) =>
          prev.map((r) => ({ ...r, data: { ...r.data, grade: editForm.grade } }))
        );
      }
      setShowEditModal(false);
      toast.success(
        cascadeGrade
          ? "Report Folder updated. Grade applied to all reports."
          : "Report Folder updated."
      );
    } catch (err) {
      console.error("Error updating Report Folder:", err);
      toast.error("Failed to update Report Folder. Please try again.");
    } finally {
      setUpdatingFolder(false);
    }
  };

  const [requestingSig, setRequestingSig] = useState(false);
  const handleRequestSignature = async () => {
    const next = !reportFolder?.signatureRequestedAt;
    setRequestingSig(true);
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}api/report-folders/${reportFolderId}/request-signature`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ requested: next }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to update signature request");
      }
      const { signatureRequestedAt } = await res.json();
      setReportFolder((prev) => (prev ? { ...prev, signatureRequestedAt } : prev));
      toast.success(next ? "Principal notified — folder is in their signing queue." : "Signature request withdrawn.");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setRequestingSig(false);
    }
  };

  const fieldLabels = REPORT_FIELD_LABELS[reportFolder?.reportType] ?? [];

  const openExportModal = async () => {
    try {
      const token = await getToken();
      const params = new URLSearchParams({
        sort: sortField,
        order: sortOrder,
        limit: 0,
      });
      const res = await fetch(
        `${BACKEND_URL}api/report-folders/${reportFolderId}?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error("Failed to fetch reports");
      const data = await res.json();
      setAllReportsForExport(data.reports || []);
    } catch {
      setAllReportsForExport(reports);
    }
    setShowExportModal(true);
  };

  if (fetchError) {
    return (
      <div className="px-10 py-9 max-w-[960px] mx-auto max-md:px-4 max-md:pt-5 max-md:pb-6">
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {fetchError}
        </div>
      </div>
    );
  }

  return (
    <div className="px-10 py-9 max-w-[960px] mx-auto max-md:px-4 max-md:pt-5 max-md:pb-6">
      <Link
        to="/report-folders"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6"
      >
        <LucideChevronLeft size={14} />
        My Report Folders
      </Link>

      <PageHeader
        title={formatGrade(reportFolder?.grade) || reportFolder?.name || "Report Folder"}
        subtitle={
          (reportFolder?.grade
            ? [reportFolder?.name, reportFolder?.schoolYear]
            : [reportFolder?.schoolYear]
          )
            .filter(Boolean)
            .join(" · ") || undefined
        }
        action={
          <div className="flex items-center gap-3 flex-wrap">
            {atLimit && (
              <span className="text-sm text-destructive">
                Report card limit reached.{" "}
                <Link to="/credits-and-billing" className="underline">
                  Upgrade
                </Link>
              </span>
            )}
            {reports.length > 0 && (
              <Button variant="outline" onClick={openExportModal}>
                <LucideDownload className="mr-2" size={16} />
                Export
              </Button>
            )}
            {fieldLabels.length > 0 && (
              <Button
                variant="outline"
                onClick={() => navigate(`/report-folders/${reportFolderId}/defaults`)}
              >
                <LucideSettings2 className="mr-2" size={16} />
                Report Defaults
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => navigate(`/report-folders/${reportFolderId}/branding`)}
            >
              <LucideImage className="mr-2" size={16} />
              Branding
            </Button>
            {reportFolder?.schoolId && (
              <Button
                variant="outline"
                onClick={handleRequestSignature}
                disabled={requestingSig}
                className={reportFolder?.signatureRequestedAt ? "border-warning-border text-warning-fg" : ""}
              >
                <LucideBell className="mr-2" size={16} />
                {reportFolder?.signatureRequestedAt ? "Signature Requested" : "Request Signature"}
              </Button>
            )}
            <Button variant="outline" onClick={() => setShowEditModal(true)}>
              <LucidePencil className="mr-2" size={16} />
              Edit
            </Button>
            <Button variant="outline" onClick={() => setShowShareModal(true)}>
              <LucideShare2 className="mr-2" size={16} />
              Share
            </Button>
            <Button onClick={openAddStudentModal} disabled={atLimit}>
              <LucidePlus className="mr-2" size={16} />
              New Report Card
            </Button>
          </div>
        }
      />

      {/* Search + sort controls */}
      {(totalReports > 0 || searchQuery) && (
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <Input
            placeholder="Search by name or OEN..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="max-w-xs"
          />
          <select
            value={`${sortField}:${sortOrder}`}
            onChange={(e) => {
              const [field, ord] = e.target.value.split(":");
              setSortField(field);
              setSortOrder(ord);
            }}
            className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 text-foreground"
          >
            <option value="oen:asc">OEN (0-9)</option>
            <option value="oen:desc">OEN (9-0)</option>
            <option value="name:asc">Name (A-Z)</option>
            <option value="name:desc">Name (Z-A)</option>
            <option value="updatedAt:desc">Recently edited</option>
            <option value="updatedAt:asc">Oldest edited</option>
          </select>
          <StudentListSizeToggle
            size={listSize}
            onChange={setListSize}
            className="ml-auto"
          />
        </div>
      )}

      {loading ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-[60px] bg-muted rounded-lg animate-pulse" />
          ))}
        </div>
      ) : reports.length === 0 && !searchQuery ? (
        <div className="text-center py-16 text-muted-foreground">
          <LucideUser size={40} className="mx-auto mb-3 opacity-40" />
          <p>No report cards yet. Add students to get started.</p>
        </div>
      ) : reports.length === 0 && searchQuery ? (
        <div className="text-center py-12 text-muted-foreground">
          <p>No students match &quot;{searchQuery}&quot;.</p>
        </div>
      ) : (
        <StudentReportList
          reports={reports}
          reportFolderId={reportFolderId}
          size={listSize}
          totalPages={totalPages}
          currentPage={reportsPage}
          onPageChange={setReportsPage}
          onDelete={handleDelete}
          onToggleComplete={handleToggleComplete}
        />
      )}

      <AddStudentModal
        open={showAddStudentModal}
        onClose={() => setShowAddStudentModal(false)}
        onSubmit={handleCreate}
        hasDefaults={Object.keys(reportFolder?.reportDefaults ?? {}).length > 0}
        existingReports={allStudentsForModal}
        loadingReports={loadingModalStudents}
      />

      <ExportModal
        open={showExportModal}
        onClose={() => setShowExportModal(false)}
        reports={allReportsForExport}
        reportFolder={reportFolder}
      />

      <ShareReportFolderModal
        open={showShareModal}
        onClose={() => setShowShareModal(false)}
        reportFolderId={reportFolderId}
        reportFolder={reportFolder}
        onReportFolderChange={setReportFolder}
        onRefresh={fetchReportFolder}
      />

      <EditReportFolderModal
        reportFolder={
          showEditModal && reportFolder
            ? { ...reportFolder, reportsCount: totalReports }
            : null
        }
        onClose={() => setShowEditModal(false)}
        onSubmit={handleUpdateFolder}
        updating={updatingFolder}
      />

      <DeleteReportModal
        open={!!cardToDelete}
        onClose={() => setCardToDelete(null)}
        onConfirm={confirmDelete}
        studentName={cardToDelete?.studentName}
        oen={cardToDelete?.data?.oen}
      />

      <LimitExceededModal
        open={!!limitMessage}
        message={limitMessage}
        onClose={() => setLimitMessage(null)}
      />
    </div>
  );
};
